/* eslint-disable no-console */
import {FC, useEffect, useRef, useState} from "react";
import {SearchInput} from "@interstate/components/SearchInput";
import {SearchOption} from "@interstate/components/SearchInput/Types/searchInputTypes";
import {useOperationalEntity} from "../../context";
import {useFlags} from "launchdarkly-react-client-sdk";
import {
  LookupOperationalEntity,
  UpdateOperationalEntityParentV2,
  UpdateOperationalEntityParentV2Data,
  UpdateOperationalEntityParentV2Variables,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  assertOperationalEntityPresent,
  SearchOperationalEntities,
  SearchOperationalEntitiesData,
  SearchOperationalEntitiesVariables
} from "../../backend";
import {features} from "../../../features";
import {UpdateParentInput} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Endpoints} from "../../../runtime";
import {useTranslation} from "react-i18next";
import {ErrorHandler, OnCompleteCallback, useDebounce} from "../../../utils";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../amplify";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  searchOperationalEntities as searchOperationalEntitiesQuery,
  updateOperationalEntityParentV2 as updateParentV2Mutation
} from "../../../amplify/graphql";

import {ModalWithAlert, useAlert} from "../../../alerting";
import "./EditParentModal.scss";

export const EditParentModalV2: FC<{
  id: string;
  currentParent?: string;
  onComplete: OnCompleteCallback;
}> = ({id, currentParent, onComplete}) => {
  const {t} = useTranslation();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [inputErrorMessage, setInputErrorMessage] = useState<string>();
  const [noResults, setNoResults] = useState(false);
  const [working, setWorking] = useState(false);
  const [value, setValue] = useState(currentParent);
  const [searchTerm, setSearchTerm] = useState(currentParent || "");
  const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
  const isSelectingRef = useRef(false);
  const hasPendingInputRef = useRef(false);
  const selectedDisplayLabelRef = useRef("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const flags = useFlags();
  const {entity, setEntity} = useOperationalEntity();
  const useSpringBffPassthrough =
    flags[features.dev.springBffPassthrough.searchAndHierarchy];

  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    document: LookupOperationalEntity,
    amplifyQuery: lookupOperationalEntityQuery,
    context: {endpoint: Endpoints.APPSYNC},
    fetchPolicy: "network-only"
  });

  const [updateParentV2] = useGraphqlMutation<
    UpdateOperationalEntityParentV2Data,
    UpdateOperationalEntityParentV2Variables
  >({
    document: UpdateOperationalEntityParentV2,
    amplifyMutation: updateParentV2Mutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const [searchEntities] = useGraphqlLazyQuery<
    SearchOperationalEntitiesData,
    SearchOperationalEntitiesVariables
  >({
    document: SearchOperationalEntities,
    amplifyQuery: searchOperationalEntitiesQuery,
    context: {
      endpoint: useSpringBffPassthrough
        ? Endpoints.APPSYNC
        : Endpoints.SPRING_BFF
    },
    fetchPolicy: "network-only"
  });

  // Stable ref to avoid infinite useEffect loop — the hook returns a new
  // function identity each render because options.context is a new object.
  const searchEntitiesRef = useRef(searchEntities);
  searchEntitiesRef.current = searchEntities;

  useEffect(() => {
    if (debouncedSearchTerm.length >= 2 && entity.tenantId) {
      const trimmed = debouncedSearchTerm.trim();
      const query = `tenantid:"${entity.tenantId}" ${trimmed}`;
      searchEntitiesRef
        .current({variables: {query}})
        .then(result => {
          const data = result.data;
          if (!data) return;
          const options: SearchOption[] = data.searchResult.results
            .filter(result => result.id !== entity.id)
            .map(result => {
              const dbaName =
                result.organizationInformation.dbaName || result.id;
              const addr = result.organizationInformation.address;
              const street = [
                addr?.streetAddressLine1,
                addr?.streetAddressLine2
              ]
                .filter(Boolean)
                .join(", ");
              const cityStateZip =
                [addr?.city, addr?.state].filter(Boolean).join(", ") +
                (addr?.zip ? ` ${addr.zip}` : "");
              const addressLine = [street, cityStateZip]
                .filter(Boolean)
                .join(", ");
              const lines = [dbaName, addressLine, result.id]
                .filter(Boolean)
                .join("\n");
              const displayLabel = addressLine
                ? `${dbaName} (${addressLine})`
                : dbaName;
              return {
                label: lines,
                id: result.id,
                displayLabel
              };
            });
          setSearchOptions(options);
          setNoResults(options.length === 0);
          setInputErrorMessage(undefined);
        })
        .catch(() => {
          setSearchOptions([]);
          setNoResults(false);
          setInputErrorMessage("Invalid search query");
        });
    } else {
      setSearchOptions([]);
      setNoResults(false);
    }
  }, [debouncedSearchTerm, entity.tenantId, entity.id]);

  const clearErrors = (): void => {
    clearAlert();
    setInputErrorMessage(undefined);
    setNoResults(false);
  };

  const handleError: ErrorHandler = (error: any) => {
    // TODO: We need a generic mechanism to indicate validation-related errors along with a reason-code
    error.message.includes("parent id:")
      ? setInputErrorMessage(error.message)
      : createErrorAlert(error);
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const saveChanges = async () => {
    if (hasPendingInputRef.current && !value) {
      setInputErrorMessage("Please select a parent from the search results");
      return;
    }
    setWorking(true);
    clearErrors();
    const parentInput: UpdateParentInput = {
      parentId: value,
      entityId: id
    };
    await updateParentV2({variables: {parentInput}})
      .then(result => {
        const entity = result.data?.result?.entity;
        entity ? setEntity(entity) : refreshEntity();
      })
      .then(() => onComplete(true))
      .catch(handleError)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };

  // Dual-value model: SearchInput displays `searchTerm` (entity name + address) but we
  // save `value` (entity ID). Because SearchInput fires both onChange and onInputChange
  // on selection, we use refs to coordinate between the two event handlers and prevent
  // onInputChange from clearing the value that onChange just set.
  const handleSearchInputChange = (event: any) => {
    const inputValue = event.target.value || "";
    setSearchTerm(inputValue);
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
    } else {
      hasPendingInputRef.current = !!inputValue;
      setValue(undefined);
    }
    if (!inputValue) {
      setSearchOptions([]);
    }
  };

  const handleSearchSelection = (event: any) => {
    const selected = event.target.value;
    if (selected?.id) {
      isSelectingRef.current = true;
      selectedDisplayLabelRef.current = selected.displayLabel || selected.label;
      setValue(selected.id);
      setSearchTerm(selectedDisplayLabelRef.current);
    }
  };

  const handleSearchClear = () => {
    hasPendingInputRef.current = false;
    selectedDisplayLabelRef.current = "";
    setSearchTerm("");
    setValue(undefined);
    setSearchOptions([]);
  };

  return (
    <ModalWithAlert
      id={"edit-parent-modal"}
      data-testid={"edit-parent-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-parent")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      <SearchInput
        id={"parent-search-input"}
        data-testid={"parent-search-input"}
        name={"parent"}
        placeholder={"Search for parent (Results restricted by tenancy)"}
        inputValue={searchTerm}
        options={searchOptions}
        disableOptionFilter={true}
        onInputChange={handleSearchInputChange}
        onChange={handleSearchSelection}
        onClearCallback={handleSearchClear}
        onBlur={() => {
          if (value && selectedDisplayLabelRef.current) {
            setSearchTerm(selectedDisplayLabelRef.current);
          }
        }}
        aria-label={t("modal.text-input.edit-parent")}
      />
      {inputErrorMessage && (
        <span className="edit-parent-error" role="alert">
          {inputErrorMessage}
        </span>
      )}
      {noResults && !inputErrorMessage && (
        <span className="edit-parent-error" role="alert">
          No results found
        </span>
      )}
    </ModalWithAlert>
  );
};
